/**
 * Pure helper functions for ManageAccountDialog.
 *
 * Extracted to module scope for reliable c8 coverage tracking
 * and better testability (no React/component dependencies).
 */

import type { LicenseResponse } from '~/billing/services/billingService'
import { isUnlimited } from '~/utils/quotaConstants'
import { formatDisplayDate } from '~/utils/timezone'

// ============================================
// Types
// ============================================

/** Unified billing history item for both subscription invoices and credit purchases */
export interface BillingHistoryItem {
  date: Date
  amount: number
  status: string
  paid: boolean
  pdfUrl?: string
  type: 'subscription' | 'credit_purchase'
  description?: string
}

// ============================================
// License Display Helpers
// ============================================

// ============================================================================
// BILLABLE FLOW LICENSE NAMES — mirrors backend BILLABLE_FLOW_LICENSE_NAMES
// (SSOT lives in packages/api/utils/quota_constants.py).
//
// These are the THREE plans whose license instances consume billable flows
// and therefore must NEVER display credits, regardless of license status
// (active or expired):
//   1. enterprise-migrate
//   2. enterprise-migrate-accelerate
//   3. enterprise-migrate-refactor
//
// All other plans display credits and never display billable flows.
// Migration Assessment (enterprise-migrate-assess) uses credits, not flows.
// ============================================================================
export const BILLABLE_FLOW_LICENSE_NAMES: ReadonlySet<string> = new Set([
  'enterprise-migrate',
  'enterprise-migrate-accelerate',
  'enterprise-migrate-refactor'
])

/** Display label listing the 3 billable-flow plans (for super-admin tooltips). */
export const BILLABLE_FLOW_PLANS_TOOLTIP =
  'Billable-flow plans (consume flows, not credits): enterprise-migrate, enterprise-migrate-accelerate, enterprise-migrate-refactor.'

/** Mirrors backend is_billable_flow_license(). Accepts a raw name or a license object. */
export const isBillableFlowLicense = (
  licenseOrName: string | null | undefined | { license_name?: string | null }
): boolean => {
  const name =
    typeof licenseOrName === 'string' || licenseOrName == null
      ? licenseOrName
      : licenseOrName.license_name
  return !!name && BILLABLE_FLOW_LICENSE_NAMES.has(name.toLowerCase())
}

/**
 * Formats the credit limit display for a license in the license table.
 *
 * "N/A" means "Not Applicable" — the billing model doesn't use this metric.
 *
 * Display logic by billing model:
 * - **Billable flow plans**: "N/A" (credits are not applicable; billing is per migration task)
 * - **Flex plans**: "N/A" (pay-as-you-go with purchased credits, no allocated limit)
 * - **Quota-based plans**: Shows the actual credit limit value
 *   - "Unlimited" if credits are unlimited (-1 or >= 999999)
 *   - Formatted number otherwise (e.g., "1,000")
 */
export const formatCreditLimit = (license: LicenseResponse) => {
  const quota = license.quota
  if (!quota) return 'N/A'

  // Billable flow plans don't use credits — credits are not applicable
  if (isBillableFlowLicense(license)) return 'N/A'

  // Flex plans: pay-as-you-go with purchased credits, no allocated limit
  if (license.license_name === 'flex') return 'N/A'

  // Quota-based plan: always show the actual credit limit value
  if (quota.unlimited_credits || isUnlimited(quota.credits_limit)) return 'Unlimited'
  return (quota.credits_limit ?? 0).toLocaleString()
}

/**
 * Formats the billable flows limit display for a license in the license table.
 *
 * "N/A" means "Not Applicable" — the billing model doesn't use this metric.
 *
 * Display logic by billing model:
 * - **Quota-based plans** (including flex): "N/A" (billable flows are not applicable)
 * - **Billable flow plans**: Shows the actual billable flows limit value
 *   - "Unlimited" if billable_flows_limit is -1 or >= 999999
 *   - Formatted number otherwise (e.g., "10")
 *
 * Examples:
 * | Plan Type         | Billing Model  | billable_flows_limit | Display     |
 * |-------------------|----------------|----------------------|-------------|
 * | Flex              | Quota-based    | null                 | "N/A"       |
 * | Pro               | Quota-based    | 0                    | "N/A"       |
 * | Enterprise-Migrate| Billable Flow  | 10                   | "10"        |
 * | Enterprise-Migrate| Billable Flow  | -1                   | "Unlimited" |
 */
export const formatBillableFlowsLimit = (license: LicenseResponse) => {
  const quota = license.quota

  // Quota-based plans don't use billable flows — billable flows are not applicable
  if (!quota || !isBillableFlowLicense(license)) return 'N/A'

  // Billable flow plan: show the actual limit value
  if (isUnlimited(quota.billable_flows_limit)) return 'Unlimited'
  return (quota.billable_flows_limit ?? 0).toLocaleString()
}

/** Format a date string for license display, returning '-' for empty values */
export const formatLicenseDate = (dateString?: string) =>
  formatDisplayDate(dateString ?? null) || '-'

/** Label the period end according to whether Stripe will renew the subscription. */
export const getLicensePeriodEndLabel = (license: LicenseResponse) => {
  if (license.is_free_license || !license.subscription_id) return 'Access Ends'
  return license.auto_renew ? 'Renews On' : 'Access Ends'
}

// ============================================
// Billing History Helpers
// ============================================

/** Merge invoices and credit purchases into unified billing history, sorted by date descending */
export function buildBillingHistory(
  invoices: Array<{
    created: string
    amount: number
    status: string
    paid: boolean
    pdf_url?: string
  }> | null,
  creditPurchases: BillingHistoryItem[]
): BillingHistoryItem[] {
  const items: BillingHistoryItem[] = []

  // Add Stripe invoices
  if (invoices) {
    for (const invoice of invoices) {
      items.push({
        date: new Date(invoice.created),
        amount: invoice.amount,
        status: invoice.status,
        paid: invoice.paid,
        pdfUrl: invoice.pdf_url,
        type: 'subscription'
      })
    }
  }

  // Add credit purchases
  items.push(...creditPurchases)

  // Sort by date descending
  items.sort((a, b) => b.date.getTime() - a.date.getTime())

  return items
}
